import { hasRole, isAdmin } from "@/services/auth.service";
import type { User } from "@/types/auth";
import { UserRole } from "@/types/user";
import { api, type PagedResult } from "@/utils/api";
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

export interface ListUsersParams {
  page: number;
  status?: "all" | "active" | "inactive";
  role?: "all" | "sales" | "salesleader";
  insightsAccess?: "all" | "yes" | "no";
  nameFilter: string;
}

export const usersApi = {
  list: async ({
    page,
    nameFilter,
    status = "all",
    role = "all",
    insightsAccess = "all",
  }: ListUsersParams): Promise<PagedResult<User>> => {
    const response = await api
      .get(`organizations/me/users/simple`, {
        searchParams: { page, nameFilter: nameFilter },
      })
      .json<PagedResult<User>>();

    let filteredResults = response.results;
    if (status === "all") {
      filteredResults = response.results;
    }
    if (status === "active") {
      filteredResults = filteredResults.filter((user) => user.active);
    } else if (status === "inactive") {
      filteredResults = filteredResults.filter((user) => !user.active);
    }

    if (role !== "all") {
      filteredResults = filteredResults.filter((user) => {
        if (role === "sales") {
          return (
            hasRole(user, UserRole.Sales) &&
            !hasRole(user, UserRole.Salesleader)
          );
        } else if (role === "salesleader") {
          return isAdmin(user);
        }
        return true;
      });
    }

    if (insightsAccess !== "all") {
      filteredResults = filteredResults.filter((user) => {
        const hasInsights =
          hasRole(user, UserRole.Admin) || hasRole(user, UserRole.User);
        if (insightsAccess === "yes") {
          return hasInsights;
        } else if (insightsAccess === "no") {
          return !hasInsights;
        }
        return true;
      });
    }

    return {
      ...response,
      results: filteredResults,
    };
  },

  delete: async (userId: User["id"]) => {
    return await api.delete(
      `organizations/me/users/${userId}?ignoreWarnings=true`,
    );
  },

  setUserState: async (userId: User["id"], active: boolean) => {
    return await api.put(`organizations/me/users/${userId}/active`, {
      json: JSON.stringify(active),
    });
  },

  getUserDetails: async (userId: User["id"]) => {
    return await api
      .get(`organizations/me/users/simple/${userId}`)
      .json<User>();
  },

  addUserRole: async (userId: User["id"], role: UserRole) => {
    return await api.put(`organizations/me/users/${userId}/role?role=${role}`);
  },

  deleteUserRole: async (userId: User["id"], role: UserRole) => {
    return await api.delete(
      `organizations/me/users/${userId}/role?role=${role}`,
    );
  },

  updateUserRoles: async (userId: User["id"], roles: UserRole[]) => {
    return await api
      .put(`organizations/me/users/${userId}/roles`, {
        json: roles,
      })
      .json<UserRole[]>();
  },

  updateUser: async (userId: User["id"], user: Partial<Pick<User, "name">>) =>
    api
      .put(`organizations/me/users/${userId}`, {
        json: user,
      })
      .json<User>(),

  inviteNewUser: async (data: {
    name: string;
    email: string;
    password?: string;
    roles: string[];
  }) => {
    return await api.post(`users?notifyUser=true`, { json: data });
  },

  /**
   * Mints a short-lived sign-in token for the user we're currently impersonating, so another Lasso
   * app can be opened as that same user. Only works while impersonating - the endpoint requires an
   * impersonatorId claim on the session, and carries it over to the new token.
   *
   * Uses `.text()` rather than `.json()` on purpose: the endpoint returns a bare string, which the
   * API serves as text/plain.
   */
  getImpersonationLoginToken: async (user: User) => {
    return await api
      .post(
        `organizations/${user.lassoOrganizationId}/users/${user.id}/impersonate/change/token`,
      )
      .text();
  },
};

export const createUsersQueryOptions = (
  params: Omit<ListUsersParams, "page">,
) =>
  infiniteQueryOptions({
    queryKey: ["users", params] as const,
    queryFn: (opts) =>
      usersApi.list({
        ...params,
        page: opts.pageParam,
      }),
    initialPageParam: 1,
    throwOnError: true,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

export const createUserQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: ["user", userId] as const,
    queryFn: () => usersApi.getUserDetails(userId),
    enabled: !!userId,
  });

export const useUserQuery = (userId: string) =>
  useQuery(createUserQueryOptions(userId));

export const useUsersQuery = ({
  nameFilter = "",
  status,
  role,
  insightsAccess,
}: Partial<Omit<ListUsersParams, "page">>) =>
  useInfiniteQuery(
    createUsersQueryOptions({
      nameFilter,
      status,
      role,
      insightsAccess,
    }),
  );
